<script lang="ts">
  import { api } from '../../lib/api';
  import { normalizePosition } from '../../../../shared/src/defaults';
  import type { PositionConfig } from '../../../../shared/src/types';
  import { clone, type Overview } from './types';

  let { data, reload }: { data: Overview; reload: () => void } = $props();
  let draft = $state<PositionConfig | null>(null);
  let error = $state<string | null>(null);
  let qr = $state<{ id: string; url: string; label: string } | null>(null);
  let tokenRole = $state<'operator' | 'viewer'>('operator');

  function edit(p: PositionConfig | null) {
    error = null;
    const next = Math.max(0, ...data.positions.map((x) => x.number)) + 1;
    draft = p ? clone(p) : normalizePosition({ number: next, name: `CAM ${next}`, sourceId: data.sources[0]?.id ?? null });
  }

  async function save() {
    if (!draft) return;
    try {
      await api.post('/api/admin/positions', draft);
      draft = null;
      reload();
    } catch (err) {
      error = (err as Error).message;
    }
  }

  async function remove(p: PositionConfig) {
    if (!confirm(`Delete ${p.name}? Its links are revoked and connected clients are disconnected.`)) return;
    await api.del(`/api/admin/positions/${p.id}`);
    reload();
  }

  // Live source switch from the list: operators' return feed changes without reconnecting.
  async function setSource(p: PositionConfig, sourceId: string) {
    await api.post('/api/admin/positions', { ...p, sourceId: sourceId || null });
    reload();
  }

  async function createToken(p: PositionConfig) {
    await api.post('/api/admin/tokens', { positionId: p.id, role: tokenRole, label: tokenRole === 'viewer' ? `${p.name} monitor` : p.name });
    reload();
  }
  async function revoke(id: string) {
    if (!confirm('Revoke this link? Devices using it are disconnected.')) return;
    await api.post(`/api/admin/tokens/${id}/revoke`);
    reload();
  }
  async function delToken(id: string) {
    await api.del(`/api/admin/tokens/${id}`);
    reload();
  }
</script>

<div class="panel">
  <div class="row">
    <h3 class="grow">Camera positions</h3>
    <label class="inline">New links are <select bind:value={tokenRole}><option value="operator">operator</option><option value="viewer">listen-only</option></select></label>
    <button class="primary" onclick={() => edit(null)}>Add position</button>
  </div>
  {#each data.positions as p (p.id)}
    {@const toks = data.tokens.filter((t) => t.positionId === p.id && !t.revoked)}
    <div class="pos">
      <div class="list-item">
        <span class="num">{p.number}</span>
        <b class="grow">{p.name}</b>
        {#if p.group}<span class="badge">{p.group}</span>{/if}
        {#if p.exclusive}<span class="badge warn">single client</span>{/if}
        {#if p.allowVideo}<span class="badge">📷 video call</span>{/if}
        <span class="muted">{data.occupancy[p.id] ?? 0} online</span>
        <span class="muted">tally {p.tally.map((m) => `${data.tallyProviders.find((t) => t.id === m.providerId)?.name ?? '?'}:${m.input}`).join(', ') || '—'}</span>
        <select value={p.sourceId ?? ''} onchange={(e) => setSource(p, e.currentTarget.value)} title="Return feed (switches live)">
          <option value="">— no feed —</option>
          {#each data.sources as s (s.id)}<option value={s.id}>{s.name}</option>{/each}
          {#each data.browserSources as b (b.id)}<option value={b.id}>📷 {b.name}{b.live ? '' : ' (offline)'}</option>{/each}
        </select>
        <button class="small" onclick={() => edit(p)}>Edit</button>
        <button class="small" onclick={() => createToken(p)}>New link</button>
        <button class="small danger" onclick={() => remove(p)}>Delete</button>
      </div>
      {#each toks as t (t.id)}
        <div class="token row">
          <span class="badge">{t.role}</span>
          <a class="mono url" href={t.url} target="_blank" rel="noreferrer">{t.url}</a>
          <button class="small" onclick={() => navigator.clipboard?.writeText(t.url)}>Copy</button>
          <button class="small" onclick={() => (qr = { id: t.id, url: t.url, label: `${p.number} · ${p.name}` })}>QR</button>
          <button class="small danger" onclick={() => revoke(t.id)}>Revoke</button>
        </div>
      {/each}
    </div>
  {:else}<p class="muted">No positions yet.</p>{/each}
  {#if data.tokens.some((t) => t.revoked)}
    <details>
      <summary class="muted">Revoked links</summary>
      {#each data.tokens.filter((t) => t.revoked) as t (t.id)}
        <div class="token row"><span class="muted">{t.positionName} · {t.role} · {new Date(t.createdAt).toLocaleString()}</span><button class="small" onclick={() => delToken(t.id)}>Remove</button></div>
      {/each}
    </details>
  {/if}
</div>

{#if qr}
  <div class="qrModal" role="presentation" onclick={() => (qr = null)}>
    <div class="qrCard">
      <img src={`/api/admin/tokens/${qr.id}/qr.svg`} alt="QR code" />
      <div class="qrLabel">{qr.label}</div>
      <div class="mono small">{qr.url}</div>
      <div class="row noprint"><button onclick={() => window.print()}>Print</button><button onclick={() => (qr = null)}>Close</button></div>
    </div>
  </div>
{/if}

{#if draft}
  <div class="panel" style="margin-top:12px">
    <h3>{data.positions.some((p) => p.id === draft!.id) ? 'Edit' : 'New'} position</h3>
    <div class="form">
      <label>Number <input type="number" bind:value={draft.number} /></label>
      <label>Name <input bind:value={draft.name} placeholder="CAM 3 – Jib" /></label>
      <label>Return feed
        <select bind:value={draft.sourceId}>
          <option value={null}>— none —</option>
          {#each data.sources as s (s.id)}<option value={s.id}>{s.name}</option>{/each}
          {#each data.browserSources as b (b.id)}<option value={b.id}>📷 {b.name}{b.live ? '' : ' (offline)'}</option>{/each}
        </select>
      </label>
      <label>Audio group
        <select bind:value={draft.group}>
          <option value={null}>— none —</option>
          {#each data.audio.groups as g (g)}<option value={g}>{g}</option>{/each}
        </select>
      </label>
      <label class="inline"><input type="checkbox" bind:checked={draft.exclusive} /> Only one client at a time</label>
      <label class="inline" title="A client here can send its camera (e.g. a commentator) and sees the video-call grid">
        <input type="checkbox" bind:checked={draft.allowVideo} /> Browser camera / video call
      </label>
      <div class="wide">
        <div class="muted">Tally inputs (live if any is on program — add key/SuperSource inputs here too)</div>
        {#each draft.tally as m, i (i)}
          <div class="row map">
            <select bind:value={m.providerId}>{#each data.tallyProviders as t (t.id)}<option value={t.id}>{t.name}</option>{/each}</select>
            <input bind:value={m.input} placeholder="input e.g. 3" style="width:120px" />
            <button class="small" onclick={() => draft!.tally.splice(i, 1)}>Remove</button>
          </div>
        {/each}
        <button class="small" disabled={!data.tallyProviders.length} onclick={() => draft!.tally.push({ providerId: data.tallyProviders[0].id, input: String(draft!.number) })}>Add tally input</button>
      </div>
    </div>
    {#if error}<p class="error">{error}</p>{/if}
    <div class="row" style="margin-top:12px"><button class="primary" onclick={save}>Save &amp; apply</button><button onclick={() => (draft = null)}>Cancel</button></div>
  </div>
{/if}

<style>
  .pos { border-bottom: 1px solid var(--line); padding-bottom: 6px; }
  .pos .list-item { border: 0; }
  .num { font-size: 1.4rem; font-weight: 900; width: 2.2em; }
  .token { padding: 2px 0 2px 2.6em; font-size: 0.85rem; }
  .url { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 520px; }
  .map { margin: 6px 0; }
  .qrModal { position: fixed; inset: 0; background: rgba(0,0,0,.8); display: grid; place-items: center; z-index: 50; }
  .qrCard { background: #fff; color: #000; padding: 24px; border-radius: 16px; display: flex; flex-direction: column; align-items: center; gap: 10px; max-width: 92vw; }
  .qrCard img { width: min(360px, 80vw); height: auto; }
  .qrLabel { font-size: 1.6rem; font-weight: 900; }
  .small { font-size: 0.75rem; word-break: break-all; }
  @media print { .noprint { display: none; } }
</style>
